import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/core';
import {
  Card as MuiCard,
  Grid,
  Typography,
  styled as MUIStyled,
} from '@material-ui/core';

import { ICustomTheme } from 'interfaces';

type Theme = {
  theme?: ICustomTheme;
};

const maxWidth = 750;

export const cssButtonsContainer = (theme: ICustomTheme): SerializedStyles =>
  css({
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(2),
    maxWidth,
    '& button': {
      marginLeft: 0,
      marginRight: theme.spacing(4),
    },
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      '& button': {
        height: '4rem',
        marginBottom: theme.spacing(1),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
      },
    },
  });

export const cssContainerCenteredButtons = (
  theme: ICustomTheme,
): SerializedStyles =>
  css({
    flexDirection: 'row-reverse',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    maxWidth,
    '& button': {
      margin: 'auto',
    },
  });

export const getCssDisabled = (theme: ICustomTheme) => ({
  '*.disabled &': {
    color: theme.palette.grey[400],
    transition: theme.transitions.create('color'),
  },
});

export const Card = styled(MuiCard)<Theme>(({ theme }) => ({
  boxShadow: theme.shadows[3],
  color: theme.colors.grey.dark,
  display: 'flex',
  flexDirection: 'column',
  marginBottom: theme.spacing(5),
  padding: `${theme.spacing(6)}px 9.25vw`,
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
  },
}));

export const Category = styled(Typography)<Theme>(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.paletteFromBO.main,
  '*.disabled &': {
    backgroundColor: theme.palette.grey[400],
  },
  borderRadius: theme.shape.borderRadius * 6,
  color: theme.colors.white,
  display: 'flex',
  fontWeight: theme.typography.fontWeightBold,
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1, 3),
  textTransform: 'uppercase',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.5, 2),
  },
}));

export const Container = styled(Grid)({
  display: 'flex',
  flexDirection: 'column',
});

export const ParticipationContainer = styled.div<Theme>(({ theme }) => ({
  marginBottom: theme.spacing(3),
  maxWidth,
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
}));

export const Sponsor = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
  fontStyle: 'italic',
  marginBottom: theme.spacing(3),
  marginLeft: theme.spacing(2),
  textAlign: 'left',
  ...getCssDisabled(theme),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(1),
    textAlign: 'center',
  },
}));

export const SponsorImage = styled.img<{ theme: ICustomTheme; src: string }>(
  ({ theme, src }) => ({
    alignSelf: 'flex-end',
    backgroundImage: `url(${src})`,
    maxHeight: 300,
    maxWidth: '100%',
    opacity: 1,
    transition: theme.transitions.create('opacity'),
    '*.disabled &': {
      opacity: 0.42,
    },
  }),
);

export const Title = styled(Typography)<Theme>(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginBottom: theme.spacing(2),
  ...getCssDisabled(theme),
}));

export const WinUpTo = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
  marginBottom: theme.spacing(2),
  ...getCssDisabled(theme),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(1),
  },
}));

export const StyledTeaser = MUIStyled('div')(() => ({
  width: '100%',
}));
